import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { CONTACT_EMAIL } from "../i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldClass =
  "w-full bg-coal-field border border-line-field rounded px-4 py-3 text-sm text-white placeholder:text-bone-mute/60 focus:outline-none focus:border-gold transition-colors";
const labelClass = "block text-xs font-mono uppercase tracking-wider text-gold-dim mb-2";

const IntakeForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    focus: "",
    format: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const focusValue = form.focus || t.intake.focusOptions[0];
  const formatValue = form.format || t.intake.formatOptions[0];

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/intake`, {
        name: form.name.trim(),
        email: form.email.trim(),
        focus: focusValue,
        format: formatValue,
        notes: form.notes.trim(),
      });
      toast.success(t.intake.successTitle, { description: t.intake.successBody });
      setForm({ name: "", email: "", focus: "", format: "", notes: "" });
    } catch (err) {
      toast.error(t.intake.errorTitle, { description: t.intake.errorBody });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-coal-hover p-8 md:p-10 rounded-xl border border-line-strong">
      <form className="space-y-6" onSubmit={handleSubmit} data-testid="intake-form">
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label htmlFor="intake-name" className={labelClass}>
              {t.intake.nameLabel}
            </label>
            <input
              type="text"
              id="intake-name"
              name="name"
              required
              minLength={2}
              maxLength={120}
              value={form.name}
              onChange={handleChange}
              className={fieldClass}
              placeholder={t.intake.namePlaceholder}
              data-testid="intake-name-input"
            />
          </div>
          <div>
            <label htmlFor="intake-email" className={labelClass}>
              {t.intake.emailLabel}
            </label>
            <input
              type="email"
              id="intake-email"
              name="email"
              required
              value={form.email}
              onChange={handleChange}
              className={fieldClass}
              placeholder={t.intake.emailPlaceholder}
              data-testid="intake-email-input"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label htmlFor="intake-focus" className={labelClass}>
              {t.intake.focusLabel}
            </label>
            <select
              id="intake-focus"
              name="focus"
              value={focusValue}
              onChange={handleChange}
              className={fieldClass}
              data-testid="intake-focus-select"
            >
              {t.intake.focusOptions.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="intake-format" className={labelClass}>
              {t.intake.formatLabel}
            </label>
            <select
              id="intake-format"
              name="format"
              value={formatValue}
              onChange={handleChange}
              className={fieldClass}
              data-testid="intake-format-select"
            >
              {t.intake.formatOptions.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="intake-notes" className={labelClass}>
            {t.intake.notesLabel}
          </label>
          <textarea
            id="intake-notes"
            name="notes"
            rows={3}
            maxLength={2000}
            value={form.notes}
            onChange={handleChange}
            className={fieldClass}
            placeholder={t.intake.notesPlaceholder}
            data-testid="intake-notes-textarea"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-4 bg-gold text-black font-semibold text-sm tracking-wider uppercase rounded hover:bg-gold-bright transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="intake-submit-button"
        >
          {submitting ? t.intake.submitting : t.intake.submit}
        </button>
      </form>

      <div className="mt-8 pt-6 border-t border-line-subtle flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-mono text-bone-mute">
        <span>{t.intake.preferEmail}</span>
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="text-white font-medium select-all hover:text-gold transition-colors break-all"
          data-testid="intake-email-link"
        >
          {CONTACT_EMAIL}
        </a>
      </div>
    </div>
  );
};

export default IntakeForm;
